import './CartTable.scss';
import React, { useState, Fragment, useEffect, useRef, useMemo } from 'react';
import { Table, Collapse, Form, Row, Col, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { LG } from 'lib/util';
import { FaAngleDown, FaAngleRight, FaEdit, FaTrashAlt, FaAngleUp, FaCheck, FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import _ from 'lodash';
import { CartTableItem, ProductInCartTable, GutteringSystemData, Unit, CartGroup } from 'lib/types';
import { useSelector } from 'react-redux';
import { AppState, dataActions } from 'reducers';
import store from 'store';
import NumberInput from 'components/NumberInput';
import ButtonComponent from 'components/ButtonComponent';
import { useHistory } from 'react-router-dom';
import { setGutterCreatorFilters, setGuttertoBeUpdated } from 'lib/communication/gutterCreator';
import { removeCartLine } from 'lib/cart-actions';
import { AssortmentSelectionState } from 'components/AssortmentSelection/AssortmentSelection';
import { cartMethods } from 'business_logic';
import ProductLinker from 'components/ProductLinker';
import ScrollUpButton from 'components/ScrollUpButton';
import { useDevice } from 'lib/hooks/useDevice';

interface ItemsExpand {
    [key: string]: {
        isExpanded: boolean;
        isAdditionalExpanded: boolean;
        mobileExpanded: boolean[];
    }
}

interface RemeberedExpanded {
    itemsExpand: ItemsExpand;
    isodCollapse: boolean;
    gutterCollapse: boolean;
}

interface CartTableProps {
    close?: () => void | null;
}

export const CART_EXPANDED = 'cart_expanded';

const CartTable = (props: CartTableProps) => {
    const { isDesktop } = useDevice();

    const headerRef = useRef<HTMLDivElement | null>(null);

    const localCart = useSelector((state: AppState) => (state.data.orders.localCart));
    const cartTableContent = useSelector((state: AppState) => state.data.orders.cartTableContent);
    const isCartFetching = useSelector((state: AppState) => state.data.orders.props.isCartFetching);

    const [similarEditQuantity, setSimilarEditQuantity] = useState(1);
    const [similarEditDimension, setSimilarEditDimension] = useState(1);
    const [similarEditUnit, setSimilarEditUnit] = useState<Unit>({ unit: 'szt', isDefault: false, converter: 1, description: 'sztuki' });
    const [similarAdditions, setSimilarAdditions] = useState(Array<boolean>());
    const [isValidDimension, setIsValidDimension] = useState(true);
    const [isSavable, setSavable] = useState(true);
    const [gutterCollapse, setGutterCollapse] = useState(false);
    const [isodCollapse, setIsodCollapse] = useState(false);
    const [editedProductOld, setEditedProductOld] = useState<ProductInCartTable | null>(null);
    const [itemsExpand, setItemsExpand] = useState<ItemsExpand>({} as ItemsExpand);
    const [maxNet, setMaxNet] = useState<number>(0);
    const [maxVat, setMaxVat] = useState<number>(0);
    const [maxGross, setMaxGross] = useState<number>(0);
    const [maxQuantity, setMaxQuantity] = useState<number>(0);
    const [maxDimension, setMaxDimension] = useState<number>(0);
    const history = useHistory<AssortmentSelectionState>();

    let lp: number = 0;

    const saveExpanded = () => {
        sessionStorage.setItem(CART_EXPANDED, JSON.stringify({ itemsExpand, isodCollapse, gutterCollapse }));
    };

    const getRememberedExpanded = (): RemeberedExpanded | null => {
        const remembered = sessionStorage.getItem(CART_EXPANDED);
        if (!remembered) return null;
        return JSON.parse(remembered) as RemeberedExpanded;
    };

    const editProduct = (product: ProductInCartTable) => {
        setSimilarEditQuantity(product.quantity);
        setSimilarEditDimension(product.dimension ?? 1);
        setSimilarAdditions(product.additionsSelected ?? []);
        setEditedProductOld(product);
        setSimilarEditUnit(product.unit);
    };

    const getEditedProductMostRecentValues = () => {
        if (editedProductOld) {
            return {
                ...editedProductOld,
                quantity: similarEditQuantity,
                dimension: editedProductOld.cartGroup === CartGroup.gutterings ? null : similarEditDimension,
                additionsSelected: similarAdditions,
                unit: similarEditUnit
            };
        }
        return null;
    };

    const saveProduct = () => {
        if (!isSavable) return;
        if (similarEditQuantity > 0) {
            const editedProduct = getEditedProductMostRecentValues();
            if (editedProduct) {
                store.dispatch(dataActions.editProductsInLocalCart({
                    index: editedProduct.indexInCart,
                    product: editedProduct
                }));
            }
            setEditedProductOld(null);
        }
    };

    const editedProductNetto = (): number => {
        const editedProduct = getEditedProductMostRecentValues();
        return editedProduct ? cartMethods.getTotalPriceNet(editedProduct) : 0;
    };

    const editedProductVat = (): number => {
        const editedProduct = getEditedProductMostRecentValues();
        return editedProduct ? cartMethods.getTotalVat(editedProduct) : 0;
    };

    const totalEditedProductPrice = (): number => {
        const editedProduct = getEditedProductMostRecentValues();
        return editedProduct ? cartMethods.getTotalPriceGross(editedProduct) : 0;
    };

    const expandAll = () => {
        setGutterCollapse(true);
        setIsodCollapse(true);
        const newExpand = { ...itemsExpand };
        _.map(newExpand, (item) => {
            item.isExpanded = true;
            item.isAdditionalExpanded = true;
        });
        setItemsExpand(newExpand);
    };

    const hideAll = () => {
        setGutterCollapse(false);
        setIsodCollapse(false);
        const newExpand = { ...itemsExpand };
        _.map(newExpand, (item) => {
            item.isExpanded = false;
            item.isAdditionalExpanded = false;
        });
        setItemsExpand(newExpand);
    };

    const widthOffset = 1;

    const maxNetWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxNet.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.CartTable-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.CartTable-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxNet]);
    const maxVatWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxVat.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.CartTable-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.CartTable-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxVat]);
    const maxPriceWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxGross.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.CartTable-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.CartTable-similarProductsTable')?.removeChild(d);
        return width === 0 ? 30 + widthOffset : width + widthOffset;
    }, [maxGross]);
    const maxQuantityWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxQuantity.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.CartTable-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.CartTable-similarProductsTable')?.removeChild(d);
        return width;
    }, [maxQuantity]);
    const maxDimensionWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxDimension.toFixed(3);
        d.classList.add('measurer');
        document.querySelector('.CartTable-similarProductsTable')?.appendChild(d);
        const { width } = d.getBoundingClientRect();
        document.querySelector('.CartTable-similarProductsTable')?.removeChild(d);
        return width;
    }, [maxDimension]);

    const renderProduct = (prd: ProductInCartTable, prdIndex: number, identifier: string, isSoloItem: boolean = false, numberOfProductsInGroup: number = 0) => {
        const isIsod = prd.cartGroup === CartGroup.isod;
        const isGutter = prd.cartGroup === CartGroup.gutterings;
        const isAdditional = Boolean(prd.mainProductSymkar);
        if (isAdditional && !isIsod) {
            lp += numberOfProductsInGroup;
        }
        const currentOrdinalNumber = !isSoloItem ? lp - numberOfProductsInGroup + prdIndex + 1 : lp;
        const editedProduct = getEditedProductMostRecentValues();
        const isEditing = _.isEqual(prd, editedProductOld);

        const getQuantityInUnitsLabel = () => (isGutter ? '' : (isEditing && editedProduct ? cartMethods.getTotalQuantityInSelectedUnit(editedProduct) : prd.quantityJm).toFixed(2));
        const getQuantityInM2Label = () => (isGutter ? '' : (isEditing && editedProduct ? cartMethods.getTotalQuantityInM2(editedProduct) : prd.quantityM2).toFixed(4));

        return (
            <tr className={(isSoloItem && !isAdditional) ? 'CartTable-expandedTr' : `CartTable-productTr ${prdIndex % 2 === 1 ? 'odd' : ''}`} key={`${identifier}-${prdIndex}`}>
                <td>{currentOrdinalNumber}.</td>
                <td>{(isGutter || isAdditional || isSoloItem) ? <ProductLinker onClick={props.close} symKar={prd.product.symKar} unit={prd.unit.unit}>{prd.product.description}</ProductLinker> : ''}</td>
                <td className='edit-td'>
                    <div className='price-td-div' style={{ width: isEditing ? 'auto' : maxQuantityWidth, marginRight: '0' }}>
                        {
                            isEditing ?
                                <NumberInput
                                    value={similarEditQuantity}
                                    setValue={(value) => {
                                        setSimilarEditQuantity(value);
                                    }}
                                    disabledEdit={similarEditQuantity === 1}
                                /> :
                                prd.quantity
                        }
                    </div>
                </td>
                <td className='edit-td'>
                    <div className='price-td-div' style={{ width: isEditing ? 'auto' : maxDimensionWidth, marginRight: '0' }}>
                        {
                            isEditing && prd.product.isDimension && prd.product.dimensionCanEdit ? (
                                <NumberInput
                                    value={similarEditDimension}
                                    setValue={(value) => setSimilarEditDimension(value)}
                                    step={0.01}
                                    min={prd.product.dimensionMin ?? 0.01}
                                    max={prd.product.dimensionMax ?? 10 ** 5}
                                    fractDigits={3}
                                    disabled={_.isEqual(prd.product.unit, 'szt') || !prd.product.dimensionCanEdit}
                                    disabledEdit={similarEditDimension === 0.01}
                                    correct={isValidDimension}
                                    setCorrect={setIsValidDimension}
                                    showMinMaxOnInvalidRange
                                />
                            ) : prd.dimension?.toFixed(3)
                        }
                    </div>
                </td>
                {_.map(cartMethods.getAllAdditionsInLocalCart(), (addition) => {
                    const prdAddIndex = _.findIndex(prd.product.additions, (prdAdd) => prdAdd.name === addition.name);
                    return (
                        <td className='foil-td edit-td' key={`${identifier}-${prdIndex}-${addition.name}`}>
                            {
                                prdAddIndex > -1 && prd.additionsSelected ?
                                    (

                                        isEditing ?
                                            <Form.Check
                                                name={addition.name}
                                                type='checkbox'
                                                defaultChecked={prd.product.additions[prdAddIndex].default || prd.additionsSelected[prdAddIndex]}
                                                onChange={() => {
                                                    const newArr = [...similarAdditions];
                                                    newArr[prdAddIndex] = !similarAdditions[prdAddIndex];
                                                    setSimilarAdditions(newArr);
                                                }}
                                                disabled={prd.product.additions[prdAddIndex].default}
                                            /> :
                                            ((prd.product.additions[prdAddIndex].default || prd.additionsSelected[prdAddIndex]) ? <FaCheck /> : <MdClose />)
                                    ) :
                                    ''
                            }
                        </td>
                    );
                })}
                <td style={{ padding: 0, textAlign: 'center' }}>
                    {
                        (isEditing && !isGutter && !isAdditional && !isIsod && prd.product.priceInfo.units.length > 1) ? (
                            <Form.Control
                                as='select'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setSimilarEditUnit(prd.product.priceInfo.units[parseInt(e.currentTarget.value)]);
                                }}
                                style={{ padding: '1px', margin: 0, height: 'auto', fontSize: '0.85rem', minWidth: '6rem' }}
                                defaultValue={prd.product.priceInfo.units.findIndex((u) => similarEditUnit.unit === u.unit)}
                            >
                                {prd.product.priceInfo.units.map((unit, index) => (
                                    <option
                                        key={`${prd.product.symKar}-${unit.unit}`}
                                        value={index}
                                    >
                                        {unit.description}
                                    </option>
                                ))}
                            </Form.Control>
                        ) : prd.unit?.unit ?? prd.product.unit
                    }
                </td>
                <td className='value-td'>{(cartMethods.getProductPriceAfterDiscountPerDefaultUnit(prd) * (prd.unit?.converter ?? 1)).toFixed(2)}</td>
                <td className='value-td'>{getQuantityInUnitsLabel()}</td>
                <td className='value-td'>{getQuantityInM2Label()}</td>
                <td className='value-td'>
                    <div className='price-td-div' style={{ width: maxNetWidth, marginRight: '0' }}>
                        {(isEditing ? editedProductNetto() : cartMethods.getTotalPriceNet(prd)).toFixed(2)}
                    </div>
                </td>
                <td className='value-td'>
                    <div className='price-td-div' style={{ width: maxVatWidth, marginRight: '0' }}>
                        {(isEditing ? editedProductVat() : cartMethods.getTotalVat(prd)).toFixed(2)}
                    </div>
                </td>
                <td className='value-td'>
                    <div className='price-td-div' style={{ width: maxPriceWidth, marginRight: '0' }}>
                        {(isEditing ? totalEditedProductPrice() : cartMethods.getTotalPriceGross(prd)).toFixed(2)}
                    </div>
                </td>
                <td onClick={() => {
                    if (isEditing) saveProduct();
                    else editProduct(prd);
                }}
                >
                    {isEditing ? <FaCheck className={`manage-cart-icon-edit fa-check ${!isSavable ? 'disabled-icon' : ''}`} /> : <FaEdit className='manage-cart-icon-edit' />}
                </td>
                <td>
                    <FaTrashAlt className='similar-products-manage-cart-icon-delete' onClick={() => { removeCartLine(prd); }} />
                </td>
            </tr>
        );
    };

    const increaseOrdinalNumber = () => {
        lp += _.reduce(
            cartTableContent.isodItems,
            (sum, item) => sum + item.products.length,
            0
        );
        return (<></>);
    };

    const renderItem = (item: CartTableItem, isGutterAssortment: boolean = false) => {
        lp += item.products.length;
        if (isGutterAssortment && !isodCollapse) {
            lp += item.products.length;
        }
        if (item.products.length === 1 && item.products[0].cartGroup !== CartGroup.gutterings) return renderProduct(item.products[0], 1, item.identifier, true, 1);

        let isGutter: boolean = false;
        let mainProductSymkar = null;
        let isAdditional: boolean = false;
        if (item.products.length > 0) {
            isGutter = item.products[0].cartGroup === CartGroup.gutterings;
            ({ mainProductSymkar } = item.products[0]);
        }
        isAdditional = mainProductSymkar !== null && mainProductSymkar !== undefined;

        const { identifier } = item;

        const { isExpanded } = itemsExpand[identifier];
        return (
            item.totalQuantity !== 0 && (
                <>
                    <tr
                        className={`CartTable-expandedTr ${isAdditional ? 'additionalExpandedTr' : ''}`}
                        onClick={() => {
                            if (itemsExpand) {
                                const newExpand = { ...itemsExpand };
                                newExpand[identifier].isExpanded = !isExpanded;
                                setItemsExpand(newExpand);
                            }
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <td>{item.products.length > 0 && (isExpanded ? <FaAngleDown /> : <FaAngleRight />)}</td>
                        <td>{item.title}</td>
                        <td className='value-td'>
                            <div className='price-td-div' style={{ width: maxQuantityWidth, marginRight: '0' }}>
                                {item.totalQuantity}
                            </div>
                        </td>
                        <td></td>
                        {_.map(cartMethods.getAllAdditionsInLocalCart(), ({ name }) => <td key={`${identifier}-addition-${name}`}></td>)}
                        <td></td>
                        <td></td>
                        <td className='value-td'>{isGutter ? '' : item.totalQuantityJM.toFixed(2)}</td>
                        <td className='value-td'>{isGutter ? '' : item.totalQuantityM2.toFixed(4)}</td>
                        <td className='value-td'>
                            <div className='price-td-div' style={{ width: maxNetWidth, marginRight: '0' }}>
                                {item.totalNetto.toFixed(2)}
                            </div>
                        </td>
                        <td className='value-td'>
                            <div className='price-td-div' style={{ width: maxVatWidth, marginRight: '0' }}>
                                {item.totalVat.toFixed(2)}
                            </div>
                        </td>
                        <td className='value-td'>
                            <div className='price-td-div' style={{ width: maxPriceWidth, marginRight: '0' }}>
                                {item.totalPrice.toFixed(2)}
                            </div>
                        </td>
                        {isGutter ?
                            <td onClick={(e) => {
                                e.stopPropagation();
                                if (props.close) {
                                    props.close();
                                }
                                setGuttertoBeUpdated(true);
                                setGutterCreatorFilters(JSON.parse(identifier) as GutteringSystemData);
                                history.push({
                                    pathname: `${process.env.PUBLIC_URL}/new-order`,
                                    state: { ...history.location.state, selectedView: 'gutters' }
                                });
                            }}
                            >
                                <FaEdit className='manage-cart-icon' />
                            </td> :
                            <td></td>}
                        <td></td>
                    </tr>
                    {isExpanded && _.map(item.products, (prd, prdIndex) => renderProduct(prd, prdIndex, identifier, false, item.products.length))}
                </>
            )
        );
    };

    const renderProductMobile = (prd: ProductInCartTable, prdIndex: number, identifier: string) => {
        const editedProduct = getEditedProductMostRecentValues();
        const isEditing = _.isEqual(editedProductOld, prd as ProductInCartTable);
        const isPrdExpanded = itemsExpand[identifier].mobileExpanded[prdIndex];
        const isAdditional = prd.mainProductSymkar !== null && prd.mainProductSymkar !== undefined;
        const isGutter = prd.cartGroup === CartGroup.gutterings;
        const isIsod = prd.cartGroup === CartGroup.isod;
        return (
            <div
                className={`CartTable-cartProductElem block-item ${isPrdExpanded ? 'rotate' : ''}`}
                style={{ padding: '0 0 1em 0' }}
                onClick={() => {
                    const newExpand = { ...itemsExpand };
                    newExpand[identifier].mobileExpanded[prdIndex] = !isPrdExpanded;
                    setItemsExpand(newExpand);
                }}
            >
                {(isGutter || isAdditional) && (
                    <Row>
                        <Col>
                            <b>{prd.product.description}</b>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        Ilość sztuk:
                        {' '}
                        <br />
                        {isEditing ? <NumberInput
                            value={similarEditQuantity}
                            setValue={(value) => setSimilarEditQuantity(value)}
                            minDesktopResolution={LG}
                            stopPropagation
                            disabledEdit={similarEditQuantity === 1}
                        /> : <b>{prd.quantity}</b>}
                    </Col>
                    {!isGutter && (
                        <Col>
                            Wymiar:
                            {' '}
                            <br />
                            {isEditing && prd.product.isDimension && prd.product.dimensionCanEdit ? <NumberInput
                                value={similarEditDimension}
                                setValue={(value) => setSimilarEditDimension(value)}
                                step={0.01}
                                min={prd.product.dimensionMin ?? 0.01}
                                max={prd.product.dimensionMax ?? 10 ** 5}
                                fractDigits={3}
                                minDesktopResolution={LG}
                                stopPropagation
                                disabled={!prd.product.dimensionCanEdit}
                                disabledEdit={similarEditDimension === 0.01}
                                correct={isValidDimension}
                                setCorrect={setIsValidDimension}
                                showMinMaxOnInvalidRange
                            /> : <b>{prd.dimension?.toFixed(3) || '1.000'}</b>}
                        </Col>
                    )}
                    {_.map(cartMethods.getAllAdditionsInLocalCart(), ({ name }, addIndex) => {
                        const prdAddIndex = _.findIndex(prd.product.additions, (prdAdd) => prdAdd.name === name);
                        return (prdAddIndex > -1 && prd.additionsSelected) ? (
                            <Col key={`${identifier}-${prdIndex}-${addIndex}`}>
                                {name}
                                :
                                <br />
                                {
                                    (isEditing ?
                                        <Form.Check
                                            name={name}
                                            type='checkbox'
                                            defaultChecked={prd.additionsSelected[prdAddIndex]}
                                            onChange={() => {
                                                const newArr = [...similarAdditions];
                                                newArr[addIndex] = !similarAdditions[addIndex];
                                                setSimilarAdditions(newArr);
                                            }}
                                            onClick={(event) => event.stopPropagation()}
                                            disabled={prd.product.additions[prdAddIndex].default}
                                        /> :
                                        (prd.additionsSelected[prdAddIndex] ? <FaCheck /> : <MdClose />)
                                    )
                                }
                            </Col>
                        ) : '';
                    })}
                </Row>
                <Collapse in={isPrdExpanded}>
                    <section className='CartTable-mobileProductExpanded'>
                        {!isGutter && (
                            <Row>
                                <Col>
                                    JM:
                                    {' '}
                                    <br />
                                    {
                                        (isEditing && !isGutter && !isAdditional && !isIsod && prd.product.priceInfo.units.length > 1) ? (
                                            <Form.Control
                                                as='select'
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setSimilarEditUnit(prd.product.priceInfo.units[parseInt(e.currentTarget.value)]);
                                                }}
                                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                                            >
                                                {prd.product.priceInfo.units.map((unit, index) => (
                                                    <option
                                                        key={`${prd.product.symKar}-${unit.unit}`}
                                                        value={index}
                                                        selected={unit.unit === similarEditUnit.unit}
                                                    >
                                                        {unit.description}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        ) : prd.unit?.unit ?? prd.product.unit
                                    }
                                </Col>
                                <Col>
                                    Ilość w JM:
                                    <br />
                                    {' '}
                                    <b>{(isEditing && editedProduct ? cartMethods.getTotalQuantityInSelectedUnit(editedProduct) : prd.quantityJm).toFixed(2)}</b>
                                </Col>
                                <Col>
                                    Ilość w m
                                    <sup>2</sup>
                                    :
                                    <br />
                                    {' '}
                                    <b>{(isEditing && editedProduct ? (cartMethods.getTotalQuantityInM2(editedProduct)) : prd.quantityM2).toFixed(4)}</b>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col>
                                Netto:
                                <br />
                                {' '}
                                <b>{(isEditing ? editedProductNetto() : cartMethods.getTotalPriceNet(prd)).toFixed(2)}</b>
                            </Col>
                            <Col>
                                VAT:
                                <br />
                                {' '}
                                <b>{(isEditing ? editedProductVat() : cartMethods.getTotalVat(prd)).toFixed(2)}</b>
                            </Col>
                            <Col>
                                Brutto:
                                <br />
                                {' '}
                                <b>{(isEditing ? totalEditedProductPrice() : cartMethods.getTotalPriceGross(prd)).toFixed(2)}</b>
                            </Col>
                        </Row>
                        <div className='CartTable-SimilarElem'>
                            {isEditing ?
                                <FaCheck
                                    className={`manage-cart-icon-edit fa-check ${!isSavable ? 'disabled-icon' : ''}`}
                                    size={22}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        saveProduct();
                                    }}
                                /> :
                                (prd.product.isEditable ?? true) && <FaEdit
                                    className='manage-cart-icon-edit'
                                    size={22}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        editProduct(prd);
                                    }}
                                />}
                            {' '}
                            <FaTrashAlt
                                className='manage-cart-icon-delete'
                                size={22}
                                onClick={() => { removeCartLine(prd); }}
                            />
                        </div>
                    </section>
                </Collapse>
            </div>
        );
    };

    const renderItemMobile = (item: CartTableItem) => {
        let isGutter: boolean = false;
        let mainProductSymkar = null;
        let isAdditional: boolean = false;
        if (item.products.length > 0) {
            isGutter = item.products[0].cartGroup === CartGroup.gutterings;
            ({ mainProductSymkar } = item.products[0]);
        }
        isAdditional = mainProductSymkar !== null && mainProductSymkar !== undefined;

        const { identifier } = item;
        const { isExpanded } = itemsExpand[identifier];

        const getUnitsLabel = () => item.products[0].product.priceInfo.units.reduce((acc, u, index) => acc + u.description + (index < item.products[0].product.priceInfo.units.length - 1 ? ', ' : ''), '');

        return (
            <div>
                <div
                    onClick={() => {
                        const newExpand = { ...itemsExpand };
                        newExpand[identifier].isExpanded = !isExpanded;
                        setItemsExpand(newExpand);
                    }}
                    className={`CartTable-assortAdditElem ${isAdditional ? 'additional-item' : ''}`}
                >
                    <div style={{ width: '92%' }}>{item.title}</div>
                    <div style={{ width: '8%' }}>{isExpanded ? <FaAngleDown size='30px' /> : <FaAngleUp size='30px' />}</div>
                </div>
                {item.products.length > 0 && isExpanded && (
                    <>
                        <div className='CartTable-collapseSummary'>
                            {!isGutter && (
                                <Row>
                                    <Col>
                                        JM:
                                        <br />
                                        {' '}
                                        <b>{getUnitsLabel()}</b>
                                    </Col>
                                    <Col>
                                        Ilość w JM:
                                        <br />
                                        {' '}
                                        <b>{item.totalQuantityJM.toFixed(2)}</b>
                                    </Col>
                                    <Col>
                                        Ilość w m
                                        <sup>2</sup>
                                        :
                                        <br />
                                        {' '}
                                        <b>{item.totalQuantityM2.toFixed(4)}</b>
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col>
                                    Netto:
                                    <br />
                                    {' '}
                                    <b>{item.totalNetto.toFixed(2)}</b>
                                </Col>
                                <Col>
                                    VAT:
                                    <br />
                                    {' '}
                                    <b>{item.totalVat.toFixed(2)}</b>
                                </Col>
                                <Col>
                                    Brutto:
                                    <br />
                                    {' '}
                                    <b>{item.totalPrice.toFixed(2)}</b>
                                </Col>
                            </Row>
                        </div>
                        {_.map(item.products, (prd, prdIndex) => <Fragment key={`${identifier}-${prdIndex}`}>{renderProductMobile(prd, prdIndex, identifier)}</Fragment>)}
                    </>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (_.isEmpty(itemsExpand) && !_.isEmpty(cartTableContent)) {
            const newExpand: ItemsExpand = {};

            _.map(cartTableContent.mainItems, (val, key) => {
                newExpand[key] = {
                    isExpanded: true,
                    isAdditionalExpanded: false,
                    mobileExpanded: Array<boolean>(val.products.length).fill(false)
                };
            });
            _.map(cartTableContent.isodItems, (val, key) => {
                newExpand[`ISOD${key}`] = {
                    isExpanded: true,
                    isAdditionalExpanded: false,
                    mobileExpanded: Array<boolean>(val.products.length).fill(false)
                };
            });
            _.map(cartTableContent.additionalItems, (val, key) => {
                _.map(val, (val2, key2) => {
                    newExpand[key + key2] = {
                        isExpanded: true,
                        isAdditionalExpanded: false,
                        mobileExpanded: Array<boolean>(val2.products.length).fill(false)
                    };
                });
            });
            _.map(cartTableContent.gutteringsItems, (val, key) => {
                newExpand[key] = {
                    isExpanded: true,
                    isAdditionalExpanded: false,
                    mobileExpanded: Array<boolean>(val.products.length).fill(false)
                };
            });

            const remeberedExpanded: RemeberedExpanded | null = getRememberedExpanded();
            if (remeberedExpanded) {
                setIsodCollapse(remeberedExpanded.isodCollapse);
                setGutterCollapse(remeberedExpanded.gutterCollapse);
                _.map(newExpand, (val, key) => {
                    if (remeberedExpanded.itemsExpand[key]) {
                        newExpand[key] = remeberedExpanded.itemsExpand[key];
                    }
                });
            }

            setItemsExpand(newExpand);
        }
    }, [cartTableContent]);

    useEffect(() => {
        if (!_.isEmpty(itemsExpand)) saveExpanded();
    }, [itemsExpand, isodCollapse, gutterCollapse]);

    useEffect(() => {
        setSavable(similarEditQuantity > 0 && similarEditDimension > 0 && isValidDimension);
    }, [similarEditDimension, similarEditQuantity, isValidDimension]);

    useEffect(() => {
        setMaxNet(_.max([+cartMethods.totalIsodNetto().toFixed(2), +cartMethods.totalGutterNetto().toFixed(2), _.reduce(cartTableContent.mainItems, (max, item) => _.max([item.totalNetto, item.additionalItemsSummary.netto, max]) || max, 0) || 0]) || 0);
        setMaxVat(_.max([+cartMethods.totalIsodVat().toFixed(2), +cartMethods.totalGutterVat().toFixed(2), _.reduce(cartTableContent.mainItems, (max, item) => _.max([item.totalVat, item.additionalItemsSummary.vat, max]) || max, 0) || 0]) || 0);
        setMaxGross(_.max([+cartMethods.totalIsodPrice().toFixed(2), +cartMethods.totalGutterPrice().toFixed(2), _.reduce(cartTableContent.mainItems, (max, item) => _.max([item.totalPrice, item.additionalItemsSummary.gross, max]) || max, 0) || 0]) || 0);
        setMaxQuantity(_.max([
            _.reduce(cartTableContent.mainItems, (max, item) => _.max([item.totalQuantity, item.additionalItemsSummary.quantity, max]) || max, 0) || 0,
            _.reduce(cartTableContent.isodItems, (max, item) => _.max([item.totalQuantity, item.additionalItemsSummary.quantity, max]) || max, 0) || 0,
            _.reduce(cartTableContent.gutteringsItems, (max, item) => _.max([item.totalQuantity, item.additionalItemsSummary.quantity, max]) || max, 0) || 0
        ]) || 0);
        setMaxDimension(
            _.max([
                ..._.map(cartTableContent.mainItems, (item) => _.max(_.map(item.products, 'dimension'))),
                ..._.map(cartTableContent.isodItems, (item) => _.max(_.map(item.products, 'dimension'))),
                ..._.map(cartTableContent.gutteringsItems, (item) => _.max(_.map(item.products, 'dimension'))),
                ..._.map(_.flatMap(cartTableContent.additionalItems, _.values), (item) => _.max(_.map(item.products, 'dimension')))
            ]) || 0
        );
    }, [cartTableContent]);

    return (
        <>
            <div className='CartTable-expand-buttons' ref={headerRef}>
                <ButtonComponent marginLeft='0' text='ROZWIŃ' onClick={expandAll} width='110px' icon={<FaArrowDown />} />
                <ButtonComponent marginRight='0' text='ZWIŃ' onClick={hideAll} width='110px' icon={<FaArrowUp />} />
            </div>
            {isDesktop ?
                <Table size='sm' borderless className='CartTable-similarProductsTable'>
                    <thead style={{ height: '42px', position: 'sticky', top: '0px' }}>
                        <tr style={{ height: '42px' }}>
                            <th>LP.</th>
                            <th style={{ width: '450px', textAlign: 'left' }}>Nazwa</th>
                            <th style={{ width: '90px' }}>Ilość [sztuk]</th>
                            <th style={{ width: '80px' }}>WYMIAR</th>
                            {_.map(cartMethods.getAllAdditionsInLocalCart(), (addition, index) => (
                                <OverlayTrigger
                                    key={`cartAddition${index}`}
                                    placement='top'
                                    overlay={<Tooltip style={{ marginBottom: '-25px' }} id={`tooltip-${addition.parId}`}>{addition.name}</Tooltip>}
                                >
                                    <th style={{ textAlign: 'center' }}>{addition.parId}</th>
                                </OverlayTrigger>
                            ))}
                            <th style={{ textAlign: 'center' }}>JM</th>
                            <th style={{ width: '60px' }}>Cena za JM</th>
                            <th>Ilość w JM</th>
                            <th>
                                ILOŚĆ w m
                                <sup>2</sup>
                            </th>
                            <th>Netto</th>
                            <th>Vat</th>
                            <th>BRUTTO</th>
                            <th style={{ width: '21px' }} />
                            <th style={{ width: '21px' }} />
                        </tr>
                    </thead>
                    {(_.isEmpty(localCart.productsInLocalCart)) || _.isEmpty(itemsExpand) ?
                        <tbody>
                            <tr style={{ textAlign: 'center' }}>
                                {isCartFetching ? <td colSpan={13}><Spinner animation='border' /></td> : <td colSpan={13}>Brak produktów w koszyku</td>}
                            </tr>
                        </tbody> :
                        <>
                            {_.map(cartTableContent.mainItems, (val, key) => {
                                const { isAdditionalExpanded } = itemsExpand[key];
                                return (
                                    <Fragment key={key}>
                                        <tbody className='CartTable-item' key={`${key}-container`}>
                                            {renderItem(val)}
                                            {cartTableContent.additionalItems[key] && [
                                                <tr
                                                    key={`${key}-additionalOvergroupTr`}
                                                    className='additionalOvergroupTr'
                                                    onClick={() => {
                                                        const newExpand = { ...itemsExpand };
                                                        newExpand[key].isAdditionalExpanded = !isAdditionalExpanded;
                                                        setItemsExpand(newExpand);
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{isAdditionalExpanded ? <FaAngleDown /> : <FaAngleRight />}</td>
                                                    <td>Asortyment dodatkowy{val.totalQuantity === 0 && (` (${val.title})`)}</td>
                                                    <td className='value-td'>
                                                        <div className='price-td-div' style={{ width: maxQuantityWidth, marginRight: '0' }}>
                                                            {val.additionalItemsSummary.quantity}
                                                        </div>
                                                    </td>
                                                    <td colSpan={4}></td>
                                                    {_.map(cartMethods.getAllAdditionsInLocalCart(), ({ name }) => <td key={`Additional-addition-${name}`}></td>)}
                                                    <td></td>
                                                    <td className='value-td'>
                                                        <div className='price-td-div' style={{ width: maxNetWidth, marginRight: '0' }}>
                                                            {val.additionalItemsSummary.netto.toFixed(2)}
                                                        </div>
                                                    </td>
                                                    <td className='value-td'>
                                                        <div className='price-td-div' style={{ width: maxVatWidth, marginRight: '0' }}>
                                                            {val.additionalItemsSummary.vat.toFixed(2)}
                                                        </div>
                                                    </td>
                                                    <td className='value-td'>
                                                        <div className='price-td-div' style={{ width: maxPriceWidth, marginRight: '0' }}>
                                                            {val.additionalItemsSummary.gross.toFixed(2)}
                                                        </div>
                                                    </td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>, (() => {
                                                    if (isAdditionalExpanded) {
                                                        let prdIndex = 1;
                                                        return _.map(Object.values(cartTableContent.additionalItems[key]), (additionalItem) => _.map(additionalItem.products, (prd) => {
                                                            prdIndex += 1;
                                                            return renderProduct(prd, prdIndex, additionalItem.identifier, additionalItem.products.length === 1, additionalItem.products.length);
                                                        }));
                                                    }
                                                    const totalProductCount = _.reduce(Object.values(cartTableContent.additionalItems[key]), (sum, additionalItem) => sum + additionalItem.products.length, 0);
                                                    lp += totalProductCount;
                                                })()]}
                                        </tbody>
                                    </Fragment>
                                );
                            })}
                            {!_.isEmpty(cartTableContent.isodItems) && (
                                <tbody className='CartTable-group'>
                                    <tr className='CartTable-expandedTr overgroup-expand' onClick={() => setIsodCollapse(!isodCollapse)} style={{ cursor: 'pointer' }}>
                                        <td>{isodCollapse ? <FaAngleDown /> : <FaAngleRight />}</td>
                                        <td>ISOD</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        {_.map(cartMethods.getAllAdditionsInLocalCart(), ({ name }) => <td key={`ISOD-addition-${name}`}></td>)}
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxNetWidth, marginRight: '0' }}>
                                                {cartMethods.totalIsodNetto().toFixed(2)}
                                            </div>
                                        </td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxVatWidth, marginRight: '0' }}>
                                                {cartMethods.totalIsodVat().toFixed(2)}
                                            </div>
                                        </td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxPriceWidth, marginRight: '0' }}>
                                                {cartMethods.totalIsodPrice().toFixed(2)}
                                            </div>
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    {isodCollapse ? _.map(cartTableContent.isodItems, (val, key) => <Fragment key={key}>{renderItem(val)}</Fragment>) : increaseOrdinalNumber()}
                                </tbody>
                            )}
                            {!_.isEmpty(cartTableContent.gutteringsItems) &&
                                <tbody className='CartTable-group'>
                                    <tr className='CartTable-expandedTr overgroup-expand' onClick={() => setGutterCollapse(!gutterCollapse)} style={{ cursor: 'pointer' }}>
                                        <td>{gutterCollapse ? <FaAngleDown /> : <FaAngleRight />}</td>
                                        <td>Kreator Rynien</td>
                                        <td></td>
                                        <td></td>
                                        {_.map(cartMethods.getAllAdditionsInLocalCart(), ({ name }) => <td key={`Gutters-addition-${name}`}></td>)}
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxNetWidth, marginRight: '0' }}>
                                                {cartMethods.totalGutterNetto().toFixed(2)}
                                            </div>
                                        </td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxVatWidth, marginRight: '0' }}>
                                                {cartMethods.totalGutterVat().toFixed(2)}
                                            </div>
                                        </td>
                                        <td className='value-td'>
                                            <div className='price-td-div' style={{ width: maxPriceWidth, marginRight: '0' }}>
                                                {cartMethods.totalGutterPrice().toFixed(2)}
                                            </div>
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    {gutterCollapse && _.map(cartTableContent.gutteringsItems, (val, key) => <Fragment key={key}>{renderItem(val)}</Fragment>)}
                                </tbody>}
                        </>}
                </Table> :
                <>
                    {(_.isEmpty(localCart.productsInLocalCart) || _.isEmpty(itemsExpand)) ? (
                        <div className='d-flex justify-content-center'>
                            <div className='d-flex CartTable-empty-info'>
                                Brak wybranych produktów.
                            </div>
                        </div>
                    ) :
                        [
                            _.map(cartTableContent.mainItems, (val, key) => {
                                const { isAdditionalExpanded } = itemsExpand[key];
                                return (
                                    <div style={{ marginBottom: '15px' }} key={key}>
                                        {renderItemMobile(val)}
                                        {Boolean(cartTableContent.additionalItems[key]) && [
                                            <div
                                                key={`${key}-additionalOvergroup-expander`}
                                                onClick={() => {
                                                    const newExpand = { ...itemsExpand };
                                                    newExpand[key].isAdditionalExpanded = !isAdditionalExpanded;
                                                    setItemsExpand(newExpand);
                                                }}
                                                className='CartTable-assortAdditElem CartTable-productGroup CartTable-additionalAssortmentContainer'
                                            >
                                                <div style={{ width: '92%' }}><b>Asortyment dodatkowy</b></div>
                                                <div style={{ width: '8%' }}>{isAdditionalExpanded ? <FaAngleDown size='30px' /> : <FaAngleUp size='30px' />}</div>
                                            </div>, (() => {
                                                if (isAdditionalExpanded) {
                                                    let prdIndex = 0;
                                                    return (
                                                        <Fragment key={`${key}-additionalOvergroup`}>
                                                            <div className='CartTable-collapseSummary'>
                                                                <Row>
                                                                    <Col>
                                                                        Netto:
                                                                        <br />
                                                                        {' '}
                                                                        <b>{val.additionalItemsSummary.netto.toFixed(2)}</b>
                                                                    </Col>
                                                                    <Col>
                                                                        VAT:
                                                                        <br />
                                                                        {' '}
                                                                        <b>{val.additionalItemsSummary.vat.toFixed(2)}</b>
                                                                    </Col>
                                                                    <Col>
                                                                        Brutto:
                                                                        <br />
                                                                        {' '}
                                                                        <b>{val.additionalItemsSummary.gross.toFixed(2)}</b>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                            <div>
                                                                {
                                                                    _.map(Object.values(cartTableContent.additionalItems[key]), (additionalItem) => _.map(additionalItem.products, (prd) => {
                                                                        prdIndex += 1;
                                                                        return (
                                                                            <Fragment key={`${key}-additional-${additionalItem.identifier}-${prd.indexInCart}`}>
                                                                                {renderProductMobile(prd, prdIndex, additionalItem.identifier)}
                                                                            </Fragment>
                                                                        );
                                                                    }))
                                                                }
                                                            </div>
                                                        </Fragment>
                                                    );
                                                }
                                            })()
                                        ]}
                                    </div>
                                );
                            }),
                            !_.isEmpty(cartTableContent.isodItems) && (
                                <div key='ISOD-overgroup' onClick={() => { setIsodCollapse(!isodCollapse); }} className='CartTable-assortAdditElem CartTable-productGroup'>
                                    <div style={{ width: '92%' }}><b>ISOD</b></div>
                                    <div style={{ width: '8%' }}>{isodCollapse ? <FaAngleDown size='30px' /> : <FaAngleUp size='30px' />}</div>
                                </div>
                            ),
                            isodCollapse && _.map(cartTableContent.isodItems, (val, key) => <Fragment key={key}>{renderItemMobile(val)}</Fragment>),
                            !_.isEmpty(cartTableContent.gutteringsItems) && (
                                <div key='Gutters-overgroup' onClick={() => { setGutterCollapse(!gutterCollapse); }} className='CartTable-assortAdditElem CartTable-productGroup'>
                                    <div style={{ width: '92%' }}><b>Orynnowanie</b></div>
                                    <div style={{ width: '8%' }}>{gutterCollapse ? <FaAngleDown size='30px' /> : <FaAngleUp size='30px' />}</div>
                                </div>
                            ),
                            gutterCollapse && _.map(cartTableContent.gutteringsItems, (val, key) => <Fragment key={key}>{renderItemMobile(val)}</Fragment>)
                        ]}
                    <ScrollUpButton bottomOffset='55px' />
                </>}
        </>
    );
};

export default CartTable;
